import React, { useState, useEffect } from 'react';
import { Shield, Lock, Eye, Wifi, Key, AlertTriangle, ShieldCheck, ShieldAlert, ShieldX } from 'lucide-react';
import InputCard from '../common/InputCard';
import TipCard from '../common/TipCard';
import ProgressBar from '../common/ProgressBar';
import { motion } from 'framer-motion';

const InternetRiskIndex = () => {
  const [passwordStrength, setPasswordStrength] = useState(50);
  const [reusedPasswords, setReusedPasswords] = useState(5);
  const [publicWifi, setPublicWifi] = useState(3);
  const [breachExposure, setBreachExposure] = useState(true);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [riskScore, setRiskScore] = useState(0);
  const [riskLevel, setRiskLevel] = useState("");
  const [securityTips, setSecurityTips] = useState([]);

  useEffect(() => {
    let score = 0;
    // Password strength (higher strength = lower risk = lower score)
    score += (100 - passwordStrength) * 0.35;
    // Reused passwords
    score += Math.min(35, reusedPasswords * 4);
    // Public WiFi usage
    score += publicWifi * 7;
    // Breach exposure
    if (breachExposure) score += 18;
    // 2FA reduces risk
    if (twoFactorEnabled) score -= 15;
    
    const finalScore = Math.min(100, Math.max(0, Math.round(score)));
    setRiskScore(finalScore);
    
    if (finalScore < 25) {
      setRiskLevel({ level: "Low", color: "text-green-600", bg: "bg-green-100", icon: ShieldCheck, message: "Your security posture is good!" });
    } else if (finalScore < 55) {
      setRiskLevel({ level: "Medium", color: "text-yellow-600", bg: "bg-yellow-100", icon: ShieldAlert, message: "Some security improvements needed" });
    } else {
      setRiskLevel({ level: "High", color: "text-red-600", bg: "bg-red-100", icon: ShieldX, message: "Urgent security actions required!" });
    }

    // Generate security tips
    const tips = [];
    if (passwordStrength < 70) tips.push("Use a strong password with 12+ characters, numbers, and symbols");
    if (reusedPasswords > 3) tips.push("Use unique passwords for each account - try a password manager");
    if (!twoFactorEnabled) tips.push("Enable 2FA on all accounts that support it");
    if (publicWifi > 2) tips.push("Use a VPN when connecting to public WiFi");
    if (breachExposure) tips.push("Change passwords for accounts exposed in data breaches");
    setSecurityTips(tips);
  }, [passwordStrength, reusedPasswords, publicWifi, breachExposure, twoFactorEnabled]);

  const getPasswordStrengthLabel = () => {
    if (passwordStrength < 30) return "Very Weak";
    if (passwordStrength < 50) return "Weak";
    if (passwordStrength < 70) return "Moderate";
    if (passwordStrength < 90) return "Strong";
    return "Very Strong";
  };

  // const getPasswordStrengthColor = () => {
  //   if (passwordStrength < 30) return "red";
  //   if (passwordStrength < 50) return "orange";
  //   if (passwordStrength < 70) return "yellow";
  //   if (passwordStrength < 90) return "blue";
  //   return "green";
  // };

  const RiskIcon = riskLevel.icon || Shield;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <InputCard title="Security Factors" icon={Shield} delay={0.1}>
          <div className="space-y-5">
            <div>
              <div className="flex justify-between mb-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Lock size={16} /> Password Strength
                </label>
                <span className={`text-sm font-semibold ${
                  passwordStrength < 30 ? "text-red-600" : 
                  passwordStrength < 50 ? "text-orange-600" : 
                  passwordStrength < 70 ? "text-yellow-600" : 
                  passwordStrength < 90 ? "text-blue-600" : "text-green-600"
                }`}>
                  {getPasswordStrengthLabel()}
                </span>
              </div>
              <input 
                type="range" 
                min="0" 
                max="100" 
                value={passwordStrength} 
                onChange={(e) => setPasswordStrength(parseInt(e.target.value))} 
                className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                style={{ 
                  background: `linear-gradient(to right, 
                    ${passwordStrength < 30 ? '#ef4444' : 
                      passwordStrength < 50 ? '#f97316' : 
                      passwordStrength < 70 ? '#eab308' : 
                      passwordStrength < 90 ? '#3b82f6' : '#10b981'} 
                    ${passwordStrength}%, #e5e7eb ${passwordStrength}%)`
                }}
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>Weak</span>
                <span>Moderate</span>
                <span>Strong</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Key size={16} /> Number of Reused Passwords
              </label>
              <input 
                type="range" 
                min="0" 
                max="20" 
                value={reusedPasswords} 
                onChange={(e) => setReusedPasswords(parseInt(e.target.value))} 
                className="w-full"
              />
              <div className="flex justify-between text-sm mt-1">
                <span className="text-green-600">0 (Best)</span>
                <span className="font-semibold text-blue-600">{reusedPasswords} accounts</span>
                <span className="text-red-600">20+ (Worst)</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Wifi size={16} /> Public WiFi Usage (days/week)
              </label>
              <div className="flex gap-2">
                {[0, 1, 2, 3, 4, 5, 6, 7].map(day => (
                  <button
                    key={day}
                    onClick={() => setPublicWifi(day)}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                      publicWifi === day 
                        ? 'bg-blue-500 text-white shadow-md' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-2">Days per week you connect to public/unsecured WiFi</p>
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors">
                <input 
                  type="checkbox" 
                  checked={breachExposure} 
                  onChange={(e) => setBreachExposure(e.target.checked)} 
                  className="w-4 h-4"
                />
                <span className="text-sm text-gray-700">My account has been in a data breach</span>
              </label>
              
              <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors">
                <input 
                  type="checkbox" 
                  checked={twoFactorEnabled} 
                  onChange={(e) => setTwoFactorEnabled(e.target.checked)} 
                  className="w-4 h-4"
                />
                <span className="text-sm text-gray-700">Two-Factor Authentication (2FA) enabled</span>
              </label>
            </div>
          </div>
        </InputCard>

        <div className="space-y-5">
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="bg-white rounded-xl shadow-sm p-6 border text-center"
          >
            <div className="flex justify-center mb-4">
              <div className={`p-5 rounded-full ${riskLevel.bg}`}>
                <RiskIcon size={64} className={riskLevel.color} />
              </div>
            </div>
            <div className={`inline-block px-4 py-1.5 rounded-full ${riskLevel.bg} ${riskLevel.color} font-semibold text-lg mb-3`}>
              {riskLevel.level} Risk
            </div>
            <p className="text-gray-600 text-sm mb-4">{riskLevel.message}</p>
            
            <div className="mt-4">
              <div className="flex justify-between text-sm mb-1">
                <span>Risk Score</span>
                <span className="font-bold">{riskScore}/100</span>
              </div>
              <ProgressBar 
                value={riskScore} 
                max={100} 
                color={riskScore < 25 ? "green" : riskScore < 55 ? "yellow" : "red"} 
              />
            </div>
          </motion.div>
        </div>
      </div>

      {securityTips.length > 0 && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm p-5 border border-blue-100"
        >
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <ShieldCheck size={18} className="text-blue-500" />
            Recommended Security Actions
          </h3>
          <div className="space-y-2">
            {securityTips.map((tip, idx) => (
              <div key={idx} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg transition-colors">
                <AlertTriangle size={16} className="text-orange-500" />
                <span className="text-gray-700">{tip}</span>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <TipCard icon={Lock} title="Use a Password Manager" text="Generate and store unique passwords for all accounts" color="blue" />
        <TipCard icon={Eye} title="Enable 2FA" text="Add an extra layer of security to your important accounts" color="green" />
        <TipCard icon={Shield} title="Use VPN" text="Always use a VPN on public WiFi networks" color="purple" />
      </div>
    </motion.div>
  );
};

export default InternetRiskIndex;